import React from 'react';
import { useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import { toast } from 'react-toastify';
import Countdown, { zeroPad } from 'react-countdown';
import { timeoutPrompt, signOut } from 'reducers/auth';
import { refreshAccessToken } from 'api';
import { useCheckSignedIn } from "helpers/useCheckSignedIn";

/**
 * Custom timeout prompt component
 */
const ContinuePrompt: React.FC<{ remaining: number; onContinue: () => void }> = ({ remaining, onContinue }) => {
  const renderer = ({ hours, minutes, seconds }: any) => (
    <span>{zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}</span>
  );

  return (
    <div className="ToastContent TimeoutPrompt" onClick={onContinue}>
      <h4>Are you still there?</h4>
      <p>
        This session will timeout in <Countdown date={Date.now() + remaining} renderer={renderer} />
      </p>
      <p>To remain signed in please click continue</p>
      <button className="Btn BtnFull BtnSmall">Continue</button>
    </div>
  );
};

/**
 * Idle session handler mounted at the app root.
 * Only prompts in the last PROMPT_DURATION of sessionTime.
 */
export function IdleSessionHandler() {
  const isSignedIn = useCheckSignedIn();
  const isSignedInRef = React.useRef(isSignedIn);
  const dispatch = useDispatch();
  const promptTimerRef = React.useRef<number>();
  const toastIdRef = React.useRef<React.ReactText>();
  const continueClicked = React.useRef(false);
  const isPromptActiveRef = React.useRef(false);
  const lastActiveTimeRef = React.useRef(Date.now());
  const promptShownAtRef = React.useRef<number | null>(null);

  // Give user 3 minutes to respond when prompted
  const PROMPT_DURATION = 3 * 60 * 1000;  // 3 minutes of prompt
  const BEFORE_PROMPT = 12 * 60 * 1000;   // 12 minutes before signout
  const TOTAL_SESSION_TIME = BEFORE_PROMPT + PROMPT_DURATION; // 15 minutes total

  // Continue handler: refresh token and reset idle timer
  const handleContinue = async () => {
    continueClicked.current = true;
    isPromptActiveRef.current = false;
    window.clearTimeout(promptTimerRef.current);
    toast.dismiss(toastIdRef.current);
    dispatch(timeoutPrompt(false));
    reset();
    lastActiveTimeRef.current = Date.now();
    promptShownAtRef.current = null; // <-- Move clearing here

    try {
      await refreshAccessToken();
      console.log('[IdleSessionHandler] Token refreshed successfully');
    } catch (error) {
      console.error('[IdleSessionHandler] Token refresh failed during continue:', error);
    }
  };

  // Fired once when truly idle (before prompt)
  const onIdle = () => {
    console.log('[IdleSessionHandler] onIdle fired', 'Remaining Time:', getRemainingTime());
    if (isPromptActiveRef.current || !isSignedInRef.current) return;

    dispatch(timeoutPrompt(true));
    isPromptActiveRef.current = true;
    promptShownAtRef.current = Date.now();
    toastIdRef.current = toast(
      <ContinuePrompt remaining={PROMPT_DURATION} onContinue={handleContinue} />,
      {
        position: 'bottom-right',
        autoClose: PROMPT_DURATION,
        hideProgressBar: true,
        closeOnClick: false,
        closeButton: false,
        pauseOnHover: false,
        draggable: false,
        onClose: () => {
          console.log('[IdleSessionHandler] Toast onClose fired. continueClicked:', continueClicked.current, 'isPromptActive:', isPromptActiveRef.current);
          if (!continueClicked.current) {
            dispatch(signOut('Your session has expired. Please sign in again.'));
          }
          continueClicked.current = false;
          isPromptActiveRef.current = false;
        }
      }
    );

    promptTimerRef.current = window.setTimeout(() => {
      console.log('[IdleSessionHandler] Fallback timer fired, sign out user');
      pause();
      toast.dismiss(toastIdRef.current);
      dispatch(signOut('Your session has expired. Please sign in again.'));
      isPromptActiveRef.current = false;
    }, PROMPT_DURATION);
  };

  // Fired when user becomes active again
  const onActive = () => {
    // console.log('User is active - Remaining Time: ' + getRemainingTime() + ' SignedIn: ' + isSignedInRef.current);
    lastActiveTimeRef.current = Date.now();
    dispatch(timeoutPrompt(false));
    window.clearTimeout(promptTimerRef.current);

    // Always call handleContinue if prompt is active
    if (isPromptActiveRef.current) {
      handleContinue();
      // No need to manually dismiss toast or set isPromptActiveRef here
      return;
    }

    // console.log('[IdleSessionHandler] onActive - b4 resetting idle timer', 'Remaining Time: ' + getRemainingTime());
    reset();
    // console.log('[IdleSessionHandler] onActive - resetting idle timer', 'Remaining Time: ' + getRemainingTime());
  };

  // Reset idle timer on any user action
  const onAction = () => {
    lastActiveTimeRef.current = Date.now();
    const now = Date.now();
    const promptRecentlyShown =
      promptShownAtRef.current && now - promptShownAtRef.current < 2000;

    // console.log('[IdleSessionHandler] onAction fired. isPromptActive:', isPromptActiveRef.current, 'continueClicked:', continueClicked.current, 'promptRecentlyShown:', promptRecentlyShown);

    if (isPromptActiveRef.current || promptRecentlyShown) {
      handleContinue();
      return;
    }
    // console.log('[IdleSessionHandler] onAction - b4 resetting idle timer', 'Remaining Time: ' + getRemainingTime());
    reset();
    // console.log('[IdleSessionHandler] onAction - resetting idle timer', 'Remaining Time: ' + getRemainingTime());
  };

  // Instantiate the idle timer hook
  const { reset, pause, getRemainingTime } = useIdleTimer({
    timeout: BEFORE_PROMPT,
    debounce: 250,
    crossTab: true,
    events: ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'],
    onIdle,
    onActive,
    onAction,
  });

  // Sleep/wake and visibility detection
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isSignedInRef.current) {
        const now = Date.now();
        const timeSinceLastActive = now - lastActiveTimeRef.current;

        console.log("[IdleSessionHandler]", `Page became visible. Time since last active: ${timeSinceLastActive}ms`);

        // ALWAYS check elapsed time regardless of useIdleTimer state
        if (timeSinceLastActive > TOTAL_SESSION_TIME) {
          console.log('[IdleSessionHandler] Session expired during background time - immediate signout');
          dispatch(signOut('Your session has expired. Please sign in again.'));
          return;
        }

        // If past idle threshold, trigger immediate prompt
        if (timeSinceLastActive > BEFORE_PROMPT) {
          console.log('[IdleSessionHandler] Triggering immediate timeout prompt due to background inactivity');
          // Force immediate idle state
          pause();
          onIdle();
          return;
        }

        // Normal case - reset timer
        lastActiveTimeRef.current = now;
        reset();
      }
    };

    const handleFocus = () => {
      if (isSignedInRef.current) {
        handleVisibilityChange();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [reset, dispatch]);

  // Optimized QuickSight iframe integration
  React.useEffect(() => {
    const qsIframe = document.querySelector('.quicksight-embedding-iframe') as HTMLIFrameElement;
    if (!qsIframe) return;

    const onDashboardEvent = () => {
      lastActiveTimeRef.current = Date.now();
      reset();
    };

    // Single event handler for iframe interactions
    const handleIframeInteraction = () => {
      try {
        qsIframe.contentWindow?.document.addEventListener('click', onDashboardEvent, { passive: true });
        qsIframe.contentWindow?.document.addEventListener('mousemove', onDashboardEvent, { passive: true });
      } catch (error) {
        // Cross-origin iframe access blocked - this is normal
        console.log('[IdleSessionHandler] QuickSight iframe cross-origin access blocked (expected)');
      }
    };

    qsIframe.addEventListener('load', handleIframeInteraction);

    return () => {
      try {
        qsIframe.contentWindow?.document.removeEventListener('click', onDashboardEvent);
        qsIframe.contentWindow?.document.removeEventListener('mousemove', onDashboardEvent);
      } catch (error) {
        // Ignore cleanup errors for cross-origin iframes
      }
    };
  }, [reset]);

  // Keep isSignedInRef in sync and handle sign-in state changes
  React.useEffect(() => {
    isSignedInRef.current = isSignedIn;

    if (!isPromptActiveRef.current) {
      if (isSignedInRef.current) {
        console.log('[IdleSessionHandler] user signed in -> resetting idle timer');
        lastActiveTimeRef.current = Date.now();
        reset();
        console.log('[IdleSessionHandler] isSignedInRef', 'Resetting idle timer', 'Remaining Time: ', getRemainingTime());
      } else {
        console.log('[IdleSessionHandler] user signed out -> pausing idle timer');
        pause();
        // Clear any active prompts on sign out
        if (isPromptActiveRef.current) {
          window.clearTimeout(promptTimerRef.current);
          toast.dismiss(toastIdRef.current);
          isPromptActiveRef.current = false;
        }
      }
    } else {
      console.log('[IdleSessionHandler] Sign-in status change during active prompt:', isSignedInRef.current);
    }
  }, [isSignedIn]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      window.clearTimeout(promptTimerRef.current);
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
    };
  }, []);

  return null;
}
